import React, { useEffect, useMemo } from 'react';
import { FieldInfo } from '../../EmployeeData';
import { FormGroup } from '../../../../../components/form/FormGroup';
import InputLabel from '../../../../../components/form/InputLabel';
import { InfoHint } from '../../../../../components/hint/InfoHint';
import {
  ExtraFieldsConfigReferenceInput,
  useExtraFieldLookupOptionsQuery,
} from '../../../__generated__/graphql';
import { SelectDropdownControlled } from '../../../../../components/form/Selects';
import { ExtraFieldContextParam } from '../types';
import { useErrorHandler } from '../../../../../hooks/useErrorHandler';
import { createInternalLookupValue } from '../extraFieldInternalValue';

type Props = {
  fieldId: string;
  fieldInfo: FieldInfo;
  contextParams?: ExtraFieldContextParam[];
  extraFieldsConfigReference?: ExtraFieldsConfigReferenceInput;
};

export const ExtraFieldLookup: React.FC<Props> = ({
  fieldId,
  contextParams,
  fieldInfo,
  extraFieldsConfigReference,
}) => {
  const { data, loading, error } = useExtraFieldLookupOptionsQuery({
    variables: {
      id: fieldId,
      contextParams: contextParams ?? [],
      extraFieldsConfigReference: extraFieldsConfigReference ?? null,
    },
  });

  const addError = useErrorHandler();

  useEffect(() => {
    if (error) {
      addError(error);
    }
  }, [error, addError]);

  const options = useMemo(() => {
    if (!data?.extraFields.extraFieldLookupOptions.length) {
      return [];
    }
    return [
      ...data.extraFields.extraFieldLookupOptions.map((x) => ({
        value: createInternalLookupValue(x),
        label: x.label,
      })),
    ];
  }, [data]);

  return (
    <FormGroup>
      <InputLabel
        label={fieldInfo.label}
        required={fieldInfo.required}
        hint={
          fieldInfo.hint ? (
            <InfoHint className="mt-2" text={fieldInfo.hint} />
          ) : undefined
        }
      />
      <SelectDropdownControlled
        isLoading={loading}
        name={fieldInfo.name}
        options={options}
        heightMode="large"
      />
    </FormGroup>
  );
};
